/**
 * Pure decision-logic helpers for the Jaguar-Owned admin listings queue and
 * review page (list.tsx, show.tsx). Kept dependency-free and side-effect-free
 * so the state machine that drives which buttons an admin sees — and the copy
 * shown for each outcome — is covered directly by decision.test.ts rather than
 * only indirectly through component tests.
 *
 * The state machine mirrors decide_business_listing() (backend/supabase/
 * migrations/20260923120000_jaguar_owned_businesses.sql) and the Edge Function
 * that calls it (business-listing-decide): `approve` / `request_changes` /
 * `reject` are decidable only from `pending`; `remove` is decidable from
 * `pending`, `changes_requested`, `approved` OR `paused`. A listing that has
 * left all of those states (`rejected` / `removed` / `closed`) has no decision
 * left to make — the Show page renders an empty state instead of buttons.
 */

export type BusinessStatus =
  | 'pending'
  | 'changes_requested'
  | 'approved'
  | 'rejected'
  | 'paused'
  | 'removed'
  | 'closed'

export type DecisionAction = 'approve' | 'request_changes' | 'reject' | 'remove'

/** Which decision buttons an admin may press for a listing in this status. */
export function allowedActions(status: BusinessStatus): DecisionAction[] {
  switch (status) {
    case 'pending':
      return ['approve', 'request_changes', 'reject', 'remove']
    case 'changes_requested':
    case 'approved':
    case 'paused':
      return ['remove']
    case 'rejected':
    case 'removed':
    case 'closed':
    default:
      return []
  }
}

/** A note is required for everything except approve. */
export function noteRequired(action: DecisionAction): boolean {
  return action !== 'approve'
}

/** Pill tone per status (listkit Tone). */
export function statusTone(status: string): 'gold' | 'green' | 'red' | 'jaguar' | 'neutral' {
  switch (status) {
    case 'pending':
    case 'changes_requested':
      return 'gold'
    case 'approved':
      return 'green'
    case 'rejected':
    case 'removed':
      return 'red'
    case 'paused':
      return 'jaguar'
    default:
      return 'neutral'
  }
}

/** Human label per status. */
export function statusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return 'Pending review'
    case 'changes_requested':
      return 'Changes requested'
    case 'approved':
      return 'Live'
    case 'rejected':
      return 'Not approved'
    case 'paused':
      return 'Paused'
    case 'removed':
      return 'Removed'
    case 'closed':
      return 'Closed'
    default:
      return status
  }
}

/** Success toast per action. */
export function successMessage(action: DecisionAction, businessName: string): string {
  switch (action) {
    case 'approve':
      return `Approved — ${businessName} is live in Jaguar-Owned.`
    case 'request_changes':
      return 'Changes requested — the owner has been notified.'
    case 'reject':
      return 'Not approved — the owner has been notified.'
    case 'remove':
      return `Removed — ${businessName} is no longer listed.`
  }
}

/** Business type label. */
export function typeLabel(type: string): string {
  switch (type) {
    case 'storefront':
      return 'Storefront'
    case 'service_area':
      return 'Service area'
    case 'online':
      return 'Online'
    default:
      return type
  }
}

/**
 * Human label per service-area slug. The `service_areas` CHECK constraint
 * (backend/supabase/migrations/20260923120000_jaguar_owned_businesses.sql)
 * allows only these four values.
 */
export function serviceAreaLabel(area: string): string {
  switch (area) {
    case 'miami-dade':
      return 'Miami-Dade'
    case 'broward':
      return 'Broward'
    case 'palm-beach':
      return 'Palm Beach'
    case 'anywhere':
      return 'Anywhere'
    default:
      return area
  }
}
